/* eslint-disable max-statements, no-underscore-dangle */
import strings from './strings.json';
import LocalisedStrings from 'react-localization';
import _toLower from 'lodash/toLower';

/**
 * Login-only locale bundle (keys used by frontend-login screens).
 * Pre-auth has no fetchLocale API, so a slim bundled JSON is required.
 * Full platform strings live in resources Localization / pf-core after login.
 * Restore previous full bundle from git if ever needed:
 *   git show HEAD:src/languages/strings.json
 */

const EMPTY_LOCALE_DATA = { en: {} };

function normalizeLocaleBundle (localeData) {
  if (
    !localeData ||
    typeof localeData !== 'object' ||
    Array.isArray(localeData) ||
    !Object.keys(localeData).length
  ) {
    return EMPTY_LOCALE_DATA;
  }
  const firstKey = Object.keys(localeData)[0];
  if (!localeData[firstKey] || typeof localeData[firstKey] !== 'object') {
    return EMPTY_LOCALE_DATA;
  }
  return localeData;
}

const allLanguages = normalizeLocaleBundle(strings);

export const language = new LocalisedStrings(allLanguages);
language.setLanguage(_toLower(require('locale2') || 'en-us'));

export function setLanguage (locale) {
  if (locale) {
    language.setLanguage(locale);
  }
}

/** Private in-memory state containers */
let _activeStringOverrides = {};
let _activeBrandName = null;

/** Module-level memoization caches to eliminate repeated storage/URL I/O */
let _cachedBrandName;
let _cachedStoredConfig;
let _cachedUrlOverrides;

export const invalidateBrandConfigCache = () => {
  _cachedBrandName = undefined;
  _cachedStoredConfig = undefined;
  _cachedUrlOverrides = undefined;
};

export const setStringOverrides = (overrides) => {
  _activeStringOverrides =
    overrides && typeof overrides === 'object' && !Array.isArray(overrides)
      ? { ...overrides }
      : {};
  invalidateBrandConfigCache();
};

export const getStringOverrides = () => ({ ..._activeStringOverrides });

export const resetStringOverrides = () => {
  _activeStringOverrides = {};
  invalidateBrandConfigCache();
};

export const setBrandName = (name) => {
  _activeBrandName = name !== null && name !== undefined ? String(name) : null;
  invalidateBrandConfigCache();
};

export const getBrandName = () => _activeBrandName;

export const resetBrandName = () => {
  _activeBrandName = null;
  invalidateBrandConfigCache();
};

/** Declarative patterns for connector word stripping in neutral mode */
const NEUTRAL_MODE_PATTERNS = [
  { pattern: /Zinierにようこそ/gi, replacement: 'ようこそ' },
  { pattern: /Zinierへようこそ/gi, replacement: 'ようこそ' },
  { pattern: /Zinierについて/gi, replacement: '概要' },
  { pattern: /Zinierに/gi, replacement: '' },
  { pattern: /Zinierへ/gi, replacement: '' },
  { pattern: /Zinier에\s*오신\s*것을\s*환영합니다/gi, replacement: '환영합니다' },
  { pattern: /Zinier에/gi, replacement: '' },
  { pattern: /\s*-\s*Zinier\b/gi, replacement: '' },
  { pattern: /\bZinier\s*-\s*/gi, replacement: '' },
  { pattern: /\bto\s+Zinier\b/gi, replacement: '' },
  { pattern: /\bbei\s+Zinier\b/gi, replacement: '' },
  { pattern: /\ba\s+Zinier\b/gi, replacement: '' },
  { pattern: /\bsur\s+Zinier\b/gi, replacement: '' },
  { pattern: /\bde\s+Zinier\b/gi, replacement: '' },
  { pattern: /\bZinier\s+de\b/gi, replacement: '' },
  { pattern: /Zinier/gi, replacement: '' }
];

/**
 * Interpolate brandName into a localized string template.
 * Matches frontend-core tools.js contract:
 * - Non-empty brandName: replaces "Zinier" with brandName in the localized string.
 *   Uses a replacer function to protect against regex replacement tokens like $1, $&.
 * - Blank/empty brandName "": strips "Zinier" and cleans up connector words cleanly.
 * - Null / undefined: returns str unchanged.
 */
export const interpolateBrandNameString = (str, brandName) => {
  if (brandName === null || brandName === undefined || typeof str !== 'string') {
    return str;
  }
  const trimmed = brandName.trim();
  if (trimmed !== '') {
    return str.replace(/Zinier/gi, () => trimmed);
  }
  return NEUTRAL_MODE_PATTERNS.reduce(
    (acc, { pattern, replacement }) => acc.replace(pattern, replacement),
    str
  )
    .replace(/\s+/g, ' ')
    .trim();
};

const getStoredBrandConfig = () => {
  if (_cachedStoredConfig !== undefined) {
    return _cachedStoredConfig;
  }
  if (typeof window === 'undefined') {
    _cachedStoredConfig = null;
    return null;
  }
  if (window.__BRAND && typeof window.__BRAND === 'object') {
    _cachedStoredConfig = window.__BRAND;
    return _cachedStoredConfig;
  }
  try {
    const raw =
      (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('__BRAND')) ||
      (typeof localStorage !== 'undefined' && localStorage.getItem('__BRAND'));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        _cachedStoredConfig = parsed;
        return _cachedStoredConfig;
      }
    }
  } catch (e) {
    // ignore
  }
  _cachedStoredConfig = null;
  return null;
};

export const BRAND_RESOLUTION_KEYS = [
  'brandName',
  'brand'
];

const getBrandFromUrl = () => {
  if (typeof window === 'undefined' || !window.location?.search) {
    return null;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    for (let i = 0; i < BRAND_RESOLUTION_KEYS.length; i += 1) {
      const key = BRAND_RESOLUTION_KEYS[i];
      if (params.has(key)) {
        return params.get(key);
      }
    }
  } catch (e) {
    // ignore
  }
  return null;
};

const resolveEffectiveBrandName = () => {
  if (_cachedBrandName !== undefined) {
    return _cachedBrandName;
  }
  if (_activeBrandName !== null && _activeBrandName !== undefined) {
    _cachedBrandName = _activeBrandName;
    return _cachedBrandName;
  }
  const fromUrl = getBrandFromUrl();
  if (fromUrl !== null) {
    _cachedBrandName = fromUrl;
    return _cachedBrandName;
  }
  const stored = getStoredBrandConfig();
  if (stored) {
    for (let i = 0; i < BRAND_RESOLUTION_KEYS.length; i += 1) {
      const val = stored[BRAND_RESOLUTION_KEYS[i]];
      if (val !== undefined && val !== null) {
        _cachedBrandName = val;
        return _cachedBrandName;
      }
    }
  }
  if (process.env.REACT_APP_BRAND_NAME !== undefined) {
    _cachedBrandName = process.env.REACT_APP_BRAND_NAME;
    return _cachedBrandName;
  }
  _cachedBrandName = null;
  return null;
};

const formatOverrideValue = (val) => {
  if (val === undefined || val === null) {
    return null;
  }
  if (typeof val === 'object' && !Array.isArray(val)) {
    const lang = typeof language?.getLanguage === 'function' ? language.getLanguage() : 'en';
    const langPrefix = lang ? lang.split('-')[0] : 'en';
    const resolvedVal = val[lang] ?? val[langPrefix] ?? val.en ?? Object.values(val)[0];
    return resolvedVal !== undefined && resolvedVal !== null ? String(resolvedVal) : null;
  }
  return String(val);
};

const getUrlOverrides = () => {
  if (_cachedUrlOverrides !== undefined) {
    return _cachedUrlOverrides;
  }
  if (typeof window !== 'undefined' && window.location?.search) {
    try {
      const params = new URLSearchParams(window.location.search);
      const rawUrlOverrides = params.get('overrides') || params.get('stringOverrides');
      if (rawUrlOverrides) {
        const parsed = JSON.parse(rawUrlOverrides);
        if (parsed && typeof parsed === 'object') {
          _cachedUrlOverrides = parsed;
          return _cachedUrlOverrides;
        }
      }
    } catch (e) {
      // ignore
    }
  }
  _cachedUrlOverrides = null;
  return null;
};

const resolveEffectiveOverride = (key) => {
  if (_activeStringOverrides[key] !== undefined && _activeStringOverrides[key] !== null) {
    return formatOverrideValue(_activeStringOverrides[key]);
  }
  const urlOverrides = getUrlOverrides();
  if (urlOverrides && urlOverrides[key] !== undefined && urlOverrides[key] !== null) {
    return formatOverrideValue(urlOverrides[key]);
  }
  const stored = getStoredBrandConfig();
  if (
    stored?.stringOverrides &&
    stored.stringOverrides[key] !== undefined &&
    stored.stringOverrides[key] !== null
  ) {
    return formatOverrideValue(stored.stringOverrides[key]);
  }
  if (process.env.REACT_APP_STRING_OVERRIDES) {
    try {
      const parsed = JSON.parse(process.env.REACT_APP_STRING_OVERRIDES);
      if (parsed && parsed[key] !== undefined && parsed[key] !== null) {
        return formatOverrideValue(parsed[key]);
      }
    } catch (e) {
      // ignore invalid json
    }
  }
  return null;
};

const persistBrandConfigAndReload = (cfg) => {
  try {
    sessionStorage.setItem('__BRAND', JSON.stringify(cfg));
    invalidateBrandConfigCache();
    if (typeof window.location?.reload === 'function') {
      window.location.reload();
    }
  } catch (e) {
    // ignore
  }
};

export const registerBrandDevTools = () => {
  if (typeof window !== 'undefined') {
    window.setBrandConfig = (cfg) => {
      persistBrandConfigAndReload(cfg);
    };
    window.setStringOverrides = (overrides) => {
      if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) {
        return;
      }
      const existing = getStoredBrandConfig() || {};
      persistBrandConfigAndReload({
        ...existing,
        stringOverrides: {
          ...(existing.stringOverrides || {}),
          ...overrides
        }
      });
    };
    window.clearBrandConfig = () => {
      try {
        sessionStorage.removeItem('__BRAND');
        localStorage.removeItem('__BRAND');
        invalidateBrandConfigCache();
        if (typeof window.location?.reload === 'function') {
          window.location.reload();
        }
      } catch (e) {
        // ignore
      }
    };
    window.clearStringOverrides = () => {
      const existing = getStoredBrandConfig() || {};
      const rest = { ...existing };
      delete rest.stringOverrides;
      persistBrandConfigAndReload(rest);
    };
  }
};

export function getLocalString (inputStr, fallback) {
  const override = resolveEffectiveOverride(inputStr);
  if (override !== null) {
    return override;
  }

  let resolved = null;
  if (language[inputStr]) {
    resolved = language[inputStr];
  } else if (fallback) {
    resolved = fallback;
  } else if (allLanguages.en && allLanguages.en[inputStr]) {
    resolved = allLanguages.en[inputStr];
  }

  if (typeof resolved === 'string') {
    return interpolateBrandNameString(resolved, resolveEffectiveBrandName());
  }

  return resolved;
}

/**
 * Resolve a partner-configurable URL without brand-name interpolation.
 * `interpolateBrandNameString` would rewrite zinier.com when brandName is set.
 */
export function getLocalUrl (inputStr, fallback) {
  const override = resolveEffectiveOverride(inputStr);
  if (override !== null) {
    return override;
  }
  if (language[inputStr]) {
    return language[inputStr];
  }
  if (allLanguages.en && allLanguages.en[inputStr]) {
    return allLanguages.en[inputStr];
  }
  if (fallback) {
    return fallback;
  }
  return null;
}
